import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Bell, MessageSquare, Sparkles, Radio, LogOut, LayoutGrid } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/layout/ThemeToggle';
import StaffAppSplash from './StaffAppSplash';
import { installHeadTags, registerStaffServiceWorker, subscribeToStaffPush, getStaffPushStatus, rememberStaffPharmacy, staffPharmacyCode, staffPharmacyName } from './lib/pwa';
import '../portal/portalCare.css';
import '../care/care.css';
import './staffApp.css';

const TABS = [
  { to: '/staff-app', end: true, label: 'Feed', icon: Radio, badgeKey: 'urgent_count' },
  { to: '/staff-app/chats', label: 'Chats', icon: MessageSquare, badgeKey: 'unread_messages' },
  { to: '/staff-app/tools', label: 'Tools', icon: LayoutGrid, badgeKey: null },
  { to: '/staff-app/duti', label: 'Duti AI', icon: Sparkles, badgeKey: null },
];

/** Bottom-tab shell for the Staff Companion. Fetches the combined feed once
 * here and shares it with child pages via router outlet context, so the tab
 * badges and the Feed page itself never double-fetch. */
export default function StaffAppShell() {
  const { user, logout } = useAuth();
  const [feed, setFeed] = useState(null);
  const [pushStatus, setPushStatus] = useState('unknown');
  const [tools, setTools] = useState(null);
  const [pharmacyCode, setPharmacyCode] = useState(staffPharmacyCode());
  const [booted, setBooted] = useState(false);
  const pollRef = useRef(null);

  const loadFeed = useCallback(async () => {
    try {
      const res = await api.get('/staff-app/feed');
      setFeed(res.data);
    } catch { /* keep showing the last known feed on a transient failure */ }
    setBooted(true);
  }, []);

  useEffect(() => installHeadTags(pharmacyCode), [pharmacyCode]);

  useEffect(() => {
    api.get('/staff-app/install').then(({ data }) => {
      rememberStaffPharmacy(data.code, data.pharmacy?.name);
      setPharmacyCode(data.code);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    registerStaffServiceWorker();
    getStaffPushStatus().then(setPushStatus);
    loadFeed();
    api.get('/staff-app/tools').then((r) => setTools(r.data)).catch(() => setTools({}));
    pollRef.current = setInterval(loadFeed, 30000);
    return () => { clearInterval(pollRef.current); };
  }, [loadFeed]);

  const enableAlerts = async () => {
    try {
      await subscribeToStaffPush(api);
      setPushStatus('subscribed');
    } catch (err) {
      setPushStatus(err?.name === 'NotAllowedError' ? 'denied' : 'unknown');
    }
  };

  return (
    <div className="staff-app min-h-screen flex flex-col bg-background text-foreground" data-testid="staff-app-shell">
      {!booted && <StaffAppSplash />}
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border sticky top-0 bg-background/95 backdrop-blur z-20">
        <div className="min-w-0">
          <p className="text-sm font-bold leading-none truncate">{staffPharmacyName() || 'Staff Companion'}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{user?.name || user?.email}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {pushStatus !== 'subscribed' && pushStatus !== 'unsupported' && (
            <button onClick={enableAlerts} data-testid="staff-app-enable-alerts"
              className="flex items-center gap-1 text-xs font-semibold text-primary border border-primary/30 rounded-full px-2.5 py-1.5 hover:bg-primary/10 transition-colors">
              <Bell className="w-3.5 h-3.5" /> Enable alerts
            </button>
          )}
          <ThemeToggle />
          <button onClick={logout} data-testid="staff-app-logout" aria-label="Log out"
            className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet context={{ feed, reloadFeed: loadFeed, tools }} />
      </main>

      <nav className="staff-app-tabbar fixed bottom-0 inset-x-0 bg-card border-t border-border flex z-20" data-testid="staff-app-tabbar">
        {TABS.map(({ to, end, label, icon: Icon, badgeKey }) => {
          const badge = badgeKey && feed ? feed[badgeKey] : 0;
          return (
            <NavLink key={to} to={to} end={end} data-testid={`staff-app-tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 relative text-[11px] font-semibold transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
              <span className="relative">
                <Icon className="w-5 h-5" />
                {!!badge && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              {label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
